import { type ChangeEvent, memo, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Button } from '../../../Controls/Button/index.tsx';
import Icons from '../../../Elements/Icons/index.ts';
import { Tooltip } from '../../../Feedback/Tooltip/index.tsx';
import { Input } from '../../Input/index.tsx';
import { DEFAULT_STATIC_SEARCH_DEBOUNCE } from '../constants.ts';
import { SearchWrapper } from '../styled.ts';
import { type Translations } from '../types.ts';

interface SearchProps {
  mode: 'selection' | 'selected';
  onDebouncedChange: (value: string) => void;
  onClear: () => void;
  disabled?: boolean;
  isInlineMode?: boolean;
  toggleInlineMode?: () => void;
  hideInlineToggle?: boolean;
  disableInlineToggle?: boolean;
  translations: Required<Translations>;
  hasLoadOptions?: boolean;
}

export const Search = memo(function Search({
  mode,
  onDebouncedChange,
  onClear,
  disabled,
  isInlineMode,
  toggleInlineMode,
  hideInlineToggle,
  disableInlineToggle,
  translations,
  hasLoadOptions = false,
}: SearchProps) {
  const { colors } = useTheme();
  const [localValue, setLocalValue] = useState<string>('');

  const isSelectionMode = mode === 'selection';
  const isInputDisabled = disabled || (isSelectionMode && isInlineMode);

  // Debounced callback for static options only
  const debouncedChange = useDebouncedCallback((value: string) => {
    if (!hasLoadOptions) {
      onDebouncedChange(value);
    }
  }, DEFAULT_STATIC_SEARCH_DEBOUNCE);

  const handleChange = useCallbackRef((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setLocalValue(value);

    if (hasLoadOptions) {
      // For async options, call immediately (parent handles debouncing)
      onDebouncedChange(value);
    } else {
      // For static options, use local debouncing
      debouncedChange(value);
    }
  });

  const handleClear = useCallbackRef(() => {
    setLocalValue('');
    onClear();
  });

  const icon = useMemo(
    () =>
      localValue ? (
        <Icons.Clear onClick={handleClear} filled size={16} color={colors.grey100_1} dataTestId={`${mode}-search-clear`} />
      ) : (
        <Icons.Search size={16} color={colors.grey100_1} />
      ),
    [colors, handleClear, localValue, mode],
  );

  return (
    <SearchWrapper>
      <Input
        value={localValue}
        onChange={handleChange}
        placeholder={mode === 'selection' ? translations.searchSelection : translations.searchSelected}
        icon={icon}
        isActionIcon={!!localValue}
        disabled={isInputDisabled}
        dataTestId={`${mode}-search`}
      />
      {isSelectionMode && toggleInlineMode && !hideInlineToggle && (
        <Tooltip tooltip={translations.toggleSelection} placement='top'>
          <Button onClick={toggleInlineMode} disabled={disableInlineToggle} ghost iconOnly dataTestId='inline-toggle'>
            <Icons.ToggleList active={isInlineMode} />
          </Button>
        </Tooltip>
      )}
    </SearchWrapper>
  );
});
